import React from 'react';
import { useTranslation } from '../../../../i18n';

export interface FastFingersGuideProps {
  successEmoji: string;
  wrongEmojis: string[];
  successPoints: number;
  wrongPenalty: number;
  /** Slightly smaller layout for the bonus intro screen. */
  compact?: boolean;
}

/**
 * Shared host guide: which emojis to tap vs avoid.
 * Used on the intro screen (before play) and during the round.
 */
export const FastFingersGuide: React.FC<FastFingersGuideProps> = ({
  successEmoji,
  wrongEmojis,
  successPoints,
  wrongPenalty,
  compact = false,
}) => {
  const { t } = useTranslation();
  const successEmojiSize = compact
    ? 'clamp(4rem, 12vw, 7rem)'
    : 'clamp(6rem, 15vw, 10rem)';
  const wrongEmojiSize = compact
    ? 'clamp(2.25rem, 6vw, 3.5rem)'
    : 'clamp(3rem, 8vw, 5rem)';
  const sectionGap = compact ? '1.25rem' : '2rem';

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sectionGap,
        padding: compact ? '0.5rem 0 0' : '1rem',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          className="fast-fingers-host-label bonus-player-hint"
          style={{
            fontSize: compact ? 'clamp(0.85rem, 1.6vw, 1.1rem)' : 'clamp(1rem, 2vw, 1.3rem)',
            fontWeight: 600,
            marginBottom: compact ? '0.6rem' : '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {t('bonus.player.fastFingers.tapThis')}
        </div>
        <div
          style={{
            fontSize: successEmojiSize,
            lineHeight: 1,
            padding: compact ? '1rem' : '1.5rem',
            backgroundColor: 'rgba(52, 211, 153, 0.15)',
            borderRadius: compact ? '16px' : '20px',
            border: '4px solid rgba(52, 211, 153, 0.4)',
            display: 'inline-block',
          }}
        >
          {successEmoji}
        </div>
        <div
          style={{
            fontSize: compact ? 'clamp(1rem, 2vw, 1.4rem)' : 'clamp(1.2rem, 2.5vw, 1.8rem)',
            fontWeight: 'bold',
            color: '#34D399',
            marginTop: '0.75rem',
          }}
        >
          {t('bonus.player.fastFingers.pointsGain', { points: successPoints })}
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div
          className="fast-fingers-host-label bonus-player-hint"
          style={{
            fontSize: compact ? 'clamp(0.8rem, 1.5vw, 1rem)' : 'clamp(0.9rem, 1.8vw, 1.2rem)',
            fontWeight: 600,
            marginBottom: compact ? '0.6rem' : '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {t('bonus.player.fastFingers.dontTapThese')}
        </div>
        <div
          style={{
            display: 'flex',
            gap: compact ? '0.75rem' : '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {wrongEmojis.map((emoji, idx) => (
            <div
              key={idx}
              style={{
                fontSize: wrongEmojiSize,
                lineHeight: 1,
                padding: compact ? '0.75rem' : '1rem',
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                borderRadius: compact ? '12px' : '16px',
                border: '3px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              {emoji}
            </div>
          ))}
        </div>
        <div
          style={{
            fontSize: compact ? 'clamp(0.95rem, 2vw, 1.3rem)' : 'clamp(1.1rem, 2.2vw, 1.6rem)',
            fontWeight: 'bold',
            color: '#EF4444',
            marginTop: '0.75rem',
          }}
        >
          {t('bonus.player.fastFingers.pointsPenalty', { points: wrongPenalty })}
        </div>
      </div>
    </div>
  );
};
